import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Sparkles, Loader2, TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Label } from "./Motion";
import ChapterMark from "./ChapterMark";
import { api } from "../../lib/api";

const AI_IMG =
  "https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGxpZ2h0fGVufDB8fHx8MTc4NTYwNDczMXww&ixlib=rb-4.1.0&q=85";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#c9a961]/60 focus:outline-none transition-colors duration-300";

const riskColor = {
  Low: "#10b981", Moderate: "#c9a961", High: "#f59e0b", Critical: "#ef4444",
};

export default function Predictor() {
  const [form, setForm] = useState({
    company: "", industry: "Retail", monthly_revenue: "", monthly_costs: "",
    bbb_rating: "B", profit_margin: "", top_challenge: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.monthly_revenue || !form.monthly_costs || !form.profit_margin) {
      toast.error("Please fill in revenue, costs and margin.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/predict", {
        ...form,
        monthly_revenue: Number(form.monthly_revenue),
        monthly_costs: Number(form.monthly_costs),
        profit_margin: Number(form.profit_margin),
      });
      setResult(data.result);
      toast.success("Forecast ready.");
    } catch (err) {
      toast.error("Analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="predictor" data-testid="predictor-section" className="relative py-28 lg:py-40 overflow-hidden bg-[#0f2624]">
      <div className="absolute inset-0 -z-10">
        <img src={AI_IMG} alt="" className="h-full w-full object-cover opacity-[0.12]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f2624] via-transparent to-[#0f2624]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <ChapterMark n="04" title="Forecast" variant="dark" className="mb-5" />
          <div className="flex items-center gap-3">
            <Sparkles className="h-4 w-4 text-[#c9a961]" />
            <Label variant="dark">Initial estimate</Label>
          </div>
          <h2 className="mt-6 font-head text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
            See Where Your<br /><span className="text-white/40">Business Could Go.</span>
          </h2>
          <p className="mt-6 text-base lg:text-lg leading-relaxed text-white/60">
            Enter a few numbers and receive an initial AI-powered view of your business performance,
            potential opportunities, and areas worth investigating.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Form */}
          <Reveal className="lg:col-span-5">
            <form
              onSubmit={submit}
              data-testid="predict-form"
              className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-8 space-y-5"
            >
              <div>
                <label className="text-xs text-white/50 mb-2 block">Company (optional)</label>
                <input data-testid="input-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Acme Co." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-white/50 mb-2 block">Industry</label>
                  <select data-testid="input-industry" className={inputCls} value={form.industry} onChange={set("industry")}>
                    {["Retail", "Hospitality", "Construction", "Professional Services", "Manufacturing", "Healthcare", "E-commerce", "Other"].map((o) => (
                      <option key={o} value={o} className="bg-[#1e3d3a]">{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-white/50 mb-2 block">BBB rating</label>
                  <select data-testid="input-bbb" className={inputCls} value={form.bbb_rating} onChange={set("bbb_rating")}>
                    {["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D+", "D", "D-", "F", "NR"].map((o) => (
                      <option key={o} value={o} className="bg-[#1e3d3a]">{o}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-white/50 mb-2 block">Monthly revenue ($)</label>
                  <input data-testid="input-revenue" type="number" className={inputCls} value={form.monthly_revenue} onChange={set("monthly_revenue")} placeholder="80000" />
                </div>
                <div>
                  <label className="text-xs text-white/50 mb-2 block">Monthly costs ($)</label>
                  <input data-testid="input-costs" type="number" className={inputCls} value={form.monthly_costs} onChange={set("monthly_costs")} placeholder="72000" />
                </div>
              </div>
              <div>
                <label className="text-xs text-white/50 mb-2 block">Current profit margin (%)</label>
                <input data-testid="input-margin" type="number" className={inputCls} value={form.profit_margin} onChange={set("profit_margin")} placeholder="10" />
              </div>
              <div>
                <label className="text-xs text-white/50 mb-2 block">Biggest challenge (optional)</label>
                <input data-testid="input-challenge" className={inputCls} value={form.top_challenge} onChange={set("top_challenge")} placeholder="High supplier costs" />
              </div>
              <button
                type="submit"
                data-testid="predict-submit"
                disabled={loading}
                className="w-full rounded-full bg-[#c9a961] text-black py-4 text-sm font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Analysing…</>) : (<>Generate forecast</>)}
              </button>
            </form>
          </Reveal>

          {/* Result */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {!result && !loading && (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="h-full min-h-[500px] rounded-3xl border border-dashed border-white/10 flex flex-col items-center justify-center text-center p-10"
                >
                  <TrendingUp className="h-10 w-10 text-white/20" strokeWidth={1} />
                  <p className="mt-6 text-white/40 max-w-xs">Your projection and recommendations will appear here.</p>
                </motion.div>
              )}

              {loading && (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="h-full min-h-[500px] rounded-3xl border border-white/10 bg-white/[0.02] flex flex-col items-center justify-center gap-4">
                  <Loader2 className="h-8 w-8 text-[#c9a961] animate-spin" />
                  <p className="text-white/50 text-sm">Modelling your recovery path…</p>
                </motion.div>
              )}

              {result && !loading && (
                <motion.div
                  key="result"
                  data-testid="predict-result"
                  initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                      <span className="text-xs text-white/50">Health score</span>
                      <div className="mt-2 font-head text-5xl font-light">{result.health_score}<span className="text-xl text-white/40">/100</span></div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                      <span className="text-xs text-white/50">Risk level</span>
                      <div className="mt-2 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" style={{ color: riskColor[result.risk_level] || "#c9a961" }} />
                        <span className="font-head text-2xl" style={{ color: riskColor[result.risk_level] || "#fff" }}>{result.risk_level}</span>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-[#10b981]/30 bg-[#10b981]/5 p-6">
                      <span className="text-xs text-white/50">Projected savings / yr</span>
                      <div className="mt-2 font-head text-3xl font-light text-[#10b981]">${Number(result.projected_savings).toLocaleString()}</div>
                    </div>
                  </div>

                  <p className="text-sm leading-relaxed text-white/70 rounded-2xl border border-white/10 bg-white/[0.03] p-6">{result.summary}</p>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/50">6-month projection</span>
                    <div className="mt-4 h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={result.projection}>
                          <defs>
                            <linearGradient id="gOpt" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                              <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gCur" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
                              <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="month" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                          <Tooltip contentStyle={{ background: "#1e3d3a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                          <Area type="monotone" dataKey="optimized" stroke="#10b981" strokeWidth={2} fill="url(#gOpt)" name="With Elevare" />
                          <Area type="monotone" dataKey="revenue" stroke="#ef4444" strokeWidth={2} fill="url(#gCur)" name="Current path" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {result.categories?.length > 0 && (
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="forecast-categories">
                      <span className="text-xs uppercase tracking-[0.2em] text-white/50">Category breakdown</span>
                      <div className="mt-4 space-y-3.5">
                        {result.categories.map((c, i) => (
                          <div key={i} data-testid={`forecast-category-${i}`}>
                            <div className="flex items-baseline justify-between gap-3">
                              <span className="text-sm text-white/85">{c.name}</span>
                              <span className="font-head text-sm text-[#c9a961] tabular-nums">{c.score}<span className="text-white/30">/100</span></span>
                            </div>
                            <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
                              <div className="h-full rounded-full bg-[#c9a961]" style={{ width: `${Math.max(0, Math.min(100, c.score))}%` }} />
                            </div>
                            {c.note && <p className="mt-1.5 text-xs leading-relaxed text-white/45">{c.note}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {result.recommendations?.map((r, i) => (
                      <div key={i} data-testid={`recommendation-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                        <h4 className="font-head text-base leading-snug text-white">{r.title}</h4>
                        <span className="mt-2 inline-block max-w-full text-[10px] uppercase tracking-wide rounded-full border border-[#c9a961]/40 text-[#c9a961] px-2 py-1 leading-normal break-words whitespace-normal">{r.impact}</span>
                        <p className="mt-3 text-sm leading-relaxed text-white/55">{r.detail}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
